import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { CLAIM } from '@constants/index'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { useLazyGetClaimRecordQuery } from '@redux/queries/claimRecords'
import { claimRecordIdSelector } from '@redux/slices/claimStatusSlice'
import { DateUtil } from '@utils/dateUtil'

type ReadyCallback = (claimRecord: ClaimRecord) => boolean
type HandleResultCallback = (claimRecord: ClaimRecord, isTimeExceeded?: boolean) => void
type ErrorCallback = (error: FetchBaseQueryError) => void
type PollFunctionOptions = {
  claimRecordId?: string
  maxWaitTime?: number
}

type PollClaimRecordFunction = (
  readyCallback: ReadyCallback,
  handleResultCallback?: HandleResultCallback | null,
  errorCallback?: ErrorCallback | null,
  options?: PollFunctionOptions
) => void

type UsePollClaimRecordReturn = {
  pollClaimRecord: PollClaimRecordFunction
  isPolling: boolean
}

export const usePollClaimRecordV2 = (
  maxTime: number,
  pollingInterval = CLAIM.POLLING_INTERVAL_MS
): UsePollClaimRecordReturn => {
  const startTimeRef = useRef<Date | null>(null)
  const [isPolling, setIsPolling] = useState(false)
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()

  const pollClaimRecord = useCallback(
    (
      readyCallback: ReadyCallback,
      handleResultCallback?: HandleResultCallback | null,
      errorCallback?: ErrorCallback | null,
      options?: PollFunctionOptions
    ) => {
      setIsPolling(true)
      startTimeRef.current = new Date()

      const poll = () => {
        const startTime = startTimeRef.current

        // Note: In some cases (e.g. sync insurance) where claim record is created just
        // prior to the polling, redux state may not have been updated yet so
        // we need to allow caller to pass in the claim record id.
        fetchClaimRecord(options?.claimRecordId ?? claimRecordId)
          .unwrap()
          .then(claimRecord => {
            if (readyCallback(claimRecord)) {
              setIsPolling(false)
              typeof handleResultCallback === 'function' && handleResultCallback(claimRecord)
            } else if (
              startTime &&
              DateUtil.isMaxTimeExceeded({ startTime, maxTime: options?.maxWaitTime ?? maxTime })
            ) {
              setIsPolling(false)
              typeof handleResultCallback === 'function' && handleResultCallback(claimRecord, true)
            } else if (startTime) {
              setTimeout(poll, pollingInterval)
            }
          })
          .catch(error => {
            setIsPolling(false)
            typeof errorCallback === 'function' && errorCallback(error)
          })
      }

      poll()
    },
    [fetchClaimRecord, claimRecordId, maxTime, pollingInterval]
  )

  // stop polling on unmount
  useEffect(() => {
    return () => {
      startTimeRef.current = null
    }
  }, [])

  return { pollClaimRecord, isPolling }
}

/**
 * @deprecated - usage should be removed and replaced with usePollClaimRecordV2 as V2 encapsulates time exceeded handling.
 */
export const usePollClaimRecord = (pollingInterval = CLAIM.POLLING_INTERVAL_MS): PollClaimRecordFunction => {
  const claimRecordId = useSelector(claimRecordIdSelector)
  const [fetchClaimRecord] = useLazyGetClaimRecordQuery()

  const pollClaimRecord = (
    readyCallback: ReadyCallback,
    handleResultCallback?: HandleResultCallback | null,
    errorCallback?: ErrorCallback | null,
    options?: PollFunctionOptions
  ) => {
    // Note: In some cases (e.g. sync insurance) where claim record is created just
    // prior to the polling, redux state may not have been updated yet so
    // we need to allow caller to pass in the claim record id.
    fetchClaimRecord(options?.claimRecordId ?? claimRecordId)
      .unwrap()
      .then(claimRecord => {
        if (readyCallback(claimRecord)) {
          typeof handleResultCallback === 'function' && handleResultCallback(claimRecord)
        } else {
          setTimeout(
            () => pollClaimRecord(readyCallback, handleResultCallback, errorCallback, options),
            pollingInterval
          )
        }
      })
      .catch(error => {
        typeof errorCallback === 'function' && errorCallback(error)
      })
  }

  return pollClaimRecord
}
